import type { Item, ItemKind, MaterialEntry } from "@/lib/catalog-api";
import {
  ItemOptions, KIND_LABELS, colorGroups, detectKind, flat, hexFor, sizeGroups, useItemOptions,
} from "@/lib/item-options";
import { MultiSelect, SearchSelect, Swatch } from "@/components/catalog/OptionPicker";

type Attrs = Partial<Pick<Item, "size" | "size_system" | "shoe_type" | "color" | "secondary_colors" |
  "materials" | "fabric_type" | "fabric_features" | "item_kind" | "material" | "color_legacy">>;

const Label = ({ children, large }: { children: React.ReactNode; large?: boolean }) => (
  <p className={large ? "text-xs font-medium uppercase tracking-[0.18em] mb-1.5"
    : "text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1"}>{children}</p>
);

const Cell = ({ label, children, hint, wide, large }: { label: string; children: React.ReactNode; hint?: React.ReactNode; wide?: boolean; large?: boolean }) => (
  <div className={wide ? "sm:col-span-2" : ""}>
    <Label large={large}>{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
  </div>
);

export function effectiveKind(item: Attrs, category: string | undefined, o: ItemOptions): ItemKind {
  return (item.item_kind as ItemKind) || detectKind(category, o);
}

export function ItemAttributeFields({ value, category, onPatch, editing = true, prefix, largeLabels }: {
  value: Attrs; category?: string; onPatch: (p: Attrs) => void; editing?: boolean; prefix: string; largeLabels?: boolean;
}) {
  const o = useItemOptions();
  if (!o) return null;
  const kind = effectiveKind(value, category, o);
  const hexOf = (v: string) => hexFor(v, o);
  const mats: MaterialEntry[] = value.materials || [];
  const system = value.size_system || o.default_shoe_system;

  if (!editing) return <AttributeDisplay value={value} kind={kind} hexOf={hexOf} />;

  const setPercent = (name: string, raw: string) => onPatch({
    materials: mats.map((m) => m.name === name ? { ...m, percent: raw === "" ? null : Math.min(100, Math.max(0, Number(raw))) } : m),
  });

  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid={`${prefix}-attributes`}>
      <Cell large={largeLabels} label="Item type" hint={value.item_kind ? undefined : `Auto-detected from category`}>
        <select data-testid={`${prefix}-kind`} value={value.item_kind || ""}
          onChange={(e) => onPatch({ item_kind: (e.target.value || null) as ItemKind | null })}
          className="h-10 w-full border border-border bg-background px-3 text-sm">
          <option value="">Auto · {KIND_LABELS[detectKind(category, o)]}</option>
          {(Object.keys(KIND_LABELS) as ItemKind[]).map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
        </select>
      </Cell>

      {kind === "shoes" ? (
        <Cell large={largeLabels} label="Shoe size" hint={
          <select data-testid={`${prefix}-size-system`} value={system}
            onChange={(e) => onPatch({ size_system: e.target.value })}
            className="bg-transparent text-[11px] text-muted-foreground border-b border-border">
            {Object.keys(o.shoe_size_systems).map((s) => <option key={s} value={s}>{s} sizing</option>)}
          </select>}>
          <SearchSelect testid={`${prefix}-size`} value={value.size} allowCustom placeholder="Select shoe size"
            groups={sizeGroups(o, "shoes", system)}
            onChange={(v) => onPatch({ size: v, size_system: v ? system : value.size_system })} />
        </Cell>
      ) : (
        <Cell large={largeLabels} label={kind === "accessory" ? "Size (optional)" : "Size"}>
          <SearchSelect testid={`${prefix}-size`} value={value.size} allowCustom
            placeholder={kind === "accessory" ? "Optional · e.g. One Size" : "Select size"}
            groups={sizeGroups(o, kind)} onChange={(v) => onPatch({ size: v, size_system: null })} />
        </Cell>
      )}

      {kind === "shoes" && (
        <Cell large={largeLabels} label="Shoe type">
          <SearchSelect testid={`${prefix}-shoe-type`} value={value.shoe_type} placeholder="Select shoe type"
            groups={flat(o.shoe_types)} onChange={(v) => onPatch({ shoe_type: v })} />
        </Cell>
      )}

      <Cell large={largeLabels} label="Primary color" hint={value.color_legacy ? `Originally entered as “${value.color_legacy}”` : undefined}>
        <SearchSelect testid={`${prefix}-color`} value={value.color} allowCustom hexOf={hexOf}
          placeholder="Select color" groups={colorGroups(o)} onChange={(v) => onPatch({ color: v })} />
      </Cell>
      <Cell large={largeLabels} label="Secondary colors">
        <MultiSelect testid={`${prefix}-secondary-colors`} values={value.secondary_colors || []} allowCustom
          hexOf={hexOf} placeholder="Add colors…" groups={colorGroups(o)}
          onChange={(v) => onPatch({ secondary_colors: v.filter((c) => c.toLowerCase() !== (value.color || "").toLowerCase()) })} />
      </Cell>

      <Cell large={largeLabels} label="Materials" wide hint={!mats.length && value.material ? `Previously recorded: “${value.material}”` : "Optional %, e.g. 95 Cotton + 5 Spandex"}>
        <MultiSelect testid={`${prefix}-materials`} values={mats.map((m) => m.name)} allowCustom
          placeholder="Add materials…" groups={flat(o.materials)}
          onChange={(names) => onPatch({ materials: names.map((n) => mats.find((m) => m.name === n) || { name: n, percent: null }) })}
          renderChipExtra={(n) => (
            <input type="number" min={0} max={100} placeholder="%" aria-label={`${n} percent`}
              data-testid={`${prefix}-material-pct-${n}`}
              value={mats.find((m) => m.name === n)?.percent ?? ""}
              onChange={(e) => setPercent(n, e.target.value)}
              className="w-11 h-5 border border-border bg-background px-1 text-[11px]" />
          )} />
      </Cell>

      {kind !== "shoes" && (
        <>
          <Cell large={largeLabels} label="Fabric type">
            <SearchSelect testid={`${prefix}-fabric-type`} value={value.fabric_type} allowCustom
              placeholder="Optional" groups={flat(o.fabric_types)} onChange={(v) => onPatch({ fabric_type: v })} />
          </Cell>
          <Cell large={largeLabels} label="Fabric features">
            <MultiSelect testid={`${prefix}-fabric-features`} values={value.fabric_features || []}
              placeholder="Optional" groups={flat(o.fabric_features)} onChange={(v) => onPatch({ fabric_features: v })} />
          </Cell>
        </>
      )}
    </div>
  );
}

function AttributeDisplay({ value, kind, hexOf }: { value: Attrs; kind: ItemKind; hexOf: (v: string) => string | undefined }) {
  const chip = (c: string) => (
    <span key={c} className="inline-flex items-center gap-1.5 border border-border px-2 py-0.5 text-xs mr-1 mb-1">
      <Swatch hex={hexOf(c)} name={c} />{c}
    </span>
  );
  const mats = value.materials || [];
  const rows: [string, React.ReactNode][] = [
    ["Item type", KIND_LABELS[kind]],
    [kind === "shoes" ? "Shoe size" : "Size", value.size ? `${value.size}${kind === "shoes" && value.size_system ? ` · ${value.size_system}` : ""}` : null],
    ...(kind === "shoes" ? [["Shoe type", value.shoe_type] as [string, React.ReactNode]] : []),
    ["Primary color", value.color ? chip(value.color) : null],
    ["Secondary colors", value.secondary_colors?.length ? value.secondary_colors.map(chip) : null],
    ["Materials", mats.length ? mats.map((m) => m.percent != null ? `${m.percent}% ${m.name}` : m.name).join(", ") : value.material],
    ["Fabric type", value.fabric_type],
    ["Fabric features", value.fabric_features?.length ? value.fabric_features.join(", ") : null],
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="item-attributes-display">
      {rows.map(([l, v]) => (
        <div key={l}><Label>{l}</Label><div className="text-sm">{v || "—"}</div></div>
      ))}
    </div>
  );
}
